import React, {useState, useEffect, useContext} from 'react';
import * as R from 'ramda';
import {Row, Col} from 'reactstrap';

import useAPI from '@/hooks/useAPI';
import {useProducerCtx} from '@/views/Layout/b2b/ProducerNavBar/state';
import {producerMdrt} from '@/b2b/apis';
import {mapWithIndex, toDollar} from '@/util';
import {useTranslation} from 'react-i18next';
import {SystemConfigCtx} from '@/contexts';

import Icon from '@/views/shared/Icon';
import DownloadFileButton from '@/views/shared/DownloadFileButton';
import Form, {ReactSelect} from '@/Form';
import styles from '@/b2b/index.module.css';

// const ApeInfo = ({items}) => {
//   const itemList = mapWithIndex((item, idx) => (
//     <tr key={idx}>
//       <td>{item.target_year}</td>
//       <td>${toDollar(item.ape)}</td>
//     </tr>
//   ))(items);
//
//   return (
//     <table className='table table-bordered' style={{width: '50%'}}>
//       <thead>
//         <tr>
//           <th className='table-active'>Year</th>
//           <th className='table-active'>
//             Annual Premium Equivalent (APE) Tracking
//           </th>
//         </tr>
//       </thead>
//       <tbody>{itemList}</tbody>
//     </table>
//   );
// };

// expected result
// +------+--------------------------------+------------------+----------------------+
// |      |                                | Requirment (B)   |                      |
// |      |                                +------------------+                      |
// | Year | Total Qualifying Credit (A)    | MDRT | COT | TOT | Balance (Next Target)|
// |------+--------------------------------+------------------+----------------------|
// | 2024 | Current mdrt-on-premium-yr2024 | Setting*         | = A-B (MDRT|COT|TOT) |
// | 2025 | Current mdrt-on-premium-yr2025 | Setting*         | = A-B (MDRT|COT|TOT) |
// +------+--------------------------------+------------------+----------------------+
const MdrtInfo = ({items}) => {
  const checkTarget = (current, ...targets) => {
    const sortedTargets = targets.sort((a, b) => a.value - b.value);
    const nextTarget = sortedTargets.find(target => current < target.value);

    if (nextTarget) {
      return {
        remaining: current - nextTarget.value,
        nextTarget: nextTarget.label, // Name of the target (e.g., MDRT, COT, TOT)
      };
    }

    // if all targets are exceeded, return '-' instead
    return {
      remaining: '-',
      nextTarget: null,
    };
  };

  const itemListPrem = mapWithIndex((item, idx) => {
    const {remaining, nextTarget} = checkTarget(
      item.mdrt_on_premium,
      {value: item.mdrt_target_prem, label: 'MDRT'},
      {value: item.mdrt_cot_target_prem, label: 'COT'},
      {value: item.mdrt_tot_target_prem, label: 'TOT'}
    );
    const targetResult =
      remaining === '-'
        ? '-' // If all targets are exceeded
        : `${toDollar(remaining)} (${nextTarget})`;

    return (
      <tr key={idx}>
        <td>{item.target_year}</td>
        <td>${toDollar(item.mdrt_on_premium)}</td>
        <td>${toDollar(item.mdrt_target_prem)}</td>
        <td>${toDollar(item.mdrt_cot_target_prem)}</td>
        <td>${toDollar(item.mdrt_tot_target_prem)}</td>
        <td>{targetResult}</td>
      </tr>
    );
  })(items);

  const itemListComm = mapWithIndex((item, idx) => {
    const {remaining, nextTarget} = checkTarget(
      item.mdrt_on_commission,
      {value: item.mdrt_target_comm, label: 'MDRT'},
      {value: item.mdrt_cot_target_comm, label: 'COT'},
      {value: item.mdrt_tot_target_comm, label: 'TOT'}
    );
    const targetResult =
      remaining === '-'
        ? '-' // If all targets are exceeded
        : `${toDollar(remaining)} (${nextTarget})`;

    return (
      <tr key={idx}>
        <td>{item.target_year}</td>
        <td>${toDollar(item.mdrt_on_commission)}</td>
        <td>${toDollar(item.mdrt_target_comm)}</td>
        <td>${toDollar(item.mdrt_cot_target_comm)}</td>
        <td>${toDollar(item.mdrt_tot_target_comm)}</td>
        <td>{targetResult}</td>
      </tr>
    );
  })(items);

  const MdrtTable = ({itemList, caption}) => (
    <table className='table table-bordered'>
      <thead>
        <tr>
          <th className='table-active' rowSpan='2' style={{width: '10%'}}>
            Year
          </th>
          <th className='table-active' rowSpan='2' style={{width: '20%'}}>
            Total Qualifying Credit
          </th>
          <th className='table-active' colSpan='3' style={{width: '50%'}}>
            Requirement ({caption})
          </th>
          <th className='table-active' rowSpan='2' style={{width: '20%'}}>
            Balance (Next Target)
          </th>
        </tr>
        <tr>
          <th className='table-active' style={{width: '10%'}}>
            MDRT
          </th>
          <th className='table-active' style={{width: '10%'}}>
            COT
          </th>
          <th className='table-active' style={{width: '10%'}}>
            TOT
          </th>
        </tr>
      </thead>
      <tbody>{itemList}</tbody>
    </table>
  );

  return (
    <>
      <MdrtTable itemList={itemListPrem} caption='Premium Credit' />
      <MdrtTable itemList={itemListComm} caption='Commission' />
    </>
  );
};

const generateYearOptions = () => {
  const currentYear = new Date().getFullYear();
  const startYear = currentYear - 1;
  const years = [];
  for (let year = startYear; year <= currentYear; year += 1) {
    years.push({value: year, label: String(year)});
  }
  return years;
};

const Mdrt = () => {
  const [producer] = useProducerCtx();
  const {result, trigger} = useAPI(producerMdrt, {pending: true});
  const items = R.pathOr([], ['data'], result);
  const {t: tr} = useTranslation('mdrt');
  const {siteName} = useContext(SystemConfigCtx);
  const mdrtLastUpdated =
    items && items.length > 0 && items[0].mdrt_last_updated_on
      ? items[0].mdrt_last_updated_on
      : '';

  const yearOptions = generateYearOptions();
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(currentYear);

  // Wrap for ReactSelect (requires array for single select to work)
  const formData = {year: [selectedYear]};
  const setFormData = callback => {
    const next = callback(formData);
    const newYear = Array.isArray(next.year) ? next.year[0] : next.year;
    setSelectedYear(newYear);
  };

  const downloadPath = `/b2b/mdrt/downline/export/${
    producer.producerId
  }?year=${selectedYear}`;

  useEffect(
    () => {
      trigger(producer.producerId, selectedYear);
    },
    [producer, selectedYear]
  );

  return (
    <>
      <Row className='align-items-center mb-2'>
        <Col md='auto'>
          <h3 className='mb-0'>
            {tr('title')} - {producer.producerName} {mdrtLastUpdated}
          </h3>
        </Col>
        <Col md='auto'>
          <Form stateHook={[formData, setFormData]}>
            <Row form className='align-items-center'>
              <Col md='auto'>
                <ReactSelect
                  name='year'
                  label=''
                  options={yearOptions}
                  isSearchable={false}
                  isClearable={false}
                  styles={{
                    container: base => ({...base, width: '100px'}),
                    singleValue: base => ({...base, fontSize: '12px'}),
                    placeholder: base => ({...base, fontSize: '12px'}),
                    option: base => ({...base, fontSize: '12px'}),
                  }}
                />
              </Col>
              <Col md='auto'>
                <DownloadFileButton
                  className={`btn btn-outline-primary btn-sm ${styles.btn_sm}`}
                  path={downloadPath}
                  useRei
                >
                  <Icon icon='file-excel' /> Export Downline
                </DownloadFileButton>
              </Col>
            </Row>
          </Form>
        </Col>
      </Row>
      <p>{tr('subtitle', {siteName})}</p>
      <MdrtInfo items={items} />
      {/* Temporary hide this section until user clarify the discrepancy between mdrt/ape */}
      {/* <ApeInfo items={items} /> */}
    </>
  );
};

export default Mdrt;
